import { Flex, Text, Badge } from '@radix-ui/themes';

interface TemplateContentRendererProps {
  content: string;
}

const TemplateContentRenderer = ({ content }: TemplateContentRendererProps) => {
  // Safe escape function to prevent XSS
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Validate template variable names to prevent injection
  const isValidVariableName = (name: string): boolean => {
    // Only allow alphanumeric characters, underscores, and hyphens
    return /^[a-zA-Z0-9_-]+$/.test(name.trim());
  };

  const parseTemplate = (text: string) => {
    const regex = /{{(.*?)}}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the variable
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      // Only add the variable if it has a valid name
      const variableName = match[1].trim();
      if (isValidVariableName(variableName)) {
        parts.push({
          type: 'variable',
          content: variableName,
        });
      } else {
        // If invalid, treat as regular text
        parts.push({
          type: 'text',
          content: match[0], // Include the full {{...}} as text
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last variable
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return parts;
  };

  const parseMarkdown = (text: string) => {
    // Handle bold text: **text** -> <strong>text</strong>
    // Only allow simple bold formatting, no nested or complex patterns
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the bold
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      // Add the bold text (content is already safe since it's from regex capture)
      parts.push({
        type: 'bold',
        content: match[1],
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last bold
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return parts;
  };

  const TemplateVariable = (props: any) => (
    <Badge variant="surface" size="1" color="blue" {...props} />
  );

  const renderTextPart = (text: string) => {
    const markdownParts = parseMarkdown(text);
    return markdownParts.map((part, index) =>
      part.type === 'bold' ? (
        <Text key={index} size="2" color="gray" weight="bold">
          {part.content}
        </Text>
      ) : (
        <Text key={index} size="2" color="gray">
          {part.content}
        </Text>
      ),
    );
  };

  // First, replace literal \n with actual newlines
  const processedContent = content.replace(/\\n/g, '\n');

  // Split content by newlines and render each line
  const lines = processedContent.split('\n');

  return (
    <Flex direction="column" gap="1">
      {lines.map((line, lineIndex) => (
        <Flex key={lineIndex} wrap="wrap" gap="1" align="center">
          {parseTemplate(line).map((part, index) =>
            part.type === 'variable' ? (
              <TemplateVariable key={index}>{part.content}</TemplateVariable>
            ) : (
              <Flex key={index} gap="1" align="center">
                {renderTextPart(part.content)}
              </Flex>
            ),
          )}
        </Flex>
      ))}
    </Flex>
  );
};

export default TemplateContentRenderer;
